import React from "react";

import {
  CreateCustomEventDto,
  CustomEventType,
} from "@intelligems/ig-types/dtos/analytics-events/custom-events";
import {
  describePreventUrlSlashMatchTypes,
  DisallowBlankValueList,
  PreventUrlSlashList,
} from "@intelligems/ig-types/dtos/url-match";
import {
  initClickEvent,
  initCollectionPageViewEvent,
  initElementViewedEvent,
  initialCustomEventStates,
  initJavascriptEvent,
  initJavascriptImportedEvent,
  initPageViewEvent,
  initProductPageViewEvent,
  initScrollDepthEvent,
} from "@src/api/initials";
import { CodeBlocks } from "@src/images/Icons/CustomEvents/CodeBlocks";
import { NestDoorbellVisitor } from "@src/images/Icons/CustomEvents/NestDoorbellVisitor";
import { StatMinus } from "@src/images/Icons/CustomEvents/StatMinus";
import { Visibility } from "@src/images/Icons/CustomEvents/Visibility";
import { WebTraffic } from "@src/images/Icons/CustomEvents/WebTraffic";
import {
  customEventsLink,
  customJavascriptEventLink,
} from "@src/utils/linkSheet";

const getSaveValidationIssues = (
  newEvent: CreateCustomEventDto | null | undefined
): string => {
  if (!newEvent) {
    return "Event data is missing.";
  }
  const initialEvent = initialCustomEventStates[newEvent.type];

  const areProductsInvalid =
    newEvent.type === "productPageView" &&
    (newEvent.settings.length === 0 ||
      newEvent.settings.some(
        (setting) => !setting.productId?.trim() // Ensure productId is non-empty
      ));
  if (areProductsInvalid) {
    return "Add at least one product.";
  }
  const hasSelectors = (
    settings: unknown
  ): settings is { selectors: string[] } =>
    typeof settings === "object" &&
    settings !== null &&
    "selectors" in settings &&
    Array.isArray((settings as any).selectors) &&
    (settings as any).selectors.every((s: unknown) => typeof s === "string");

  const areSelectorsEmpty =
    (newEvent.type === "elementViewed" || newEvent.type === "clickEvent") &&
    (!hasSelectors(newEvent.settings) ||
      newEvent.settings.selectors.length === 0 ||
      newEvent.settings.selectors.some((selector: string) => !selector.trim()));
  if (areSelectorsEmpty) {
    return "Ensure all selectors are non-empty.";
  }

  if (hasSelectors(newEvent.settings)) {
    try {
      newEvent.settings.selectors.forEach((selector) => {
        document.querySelector(selector); // Will throw if invalid
      });
    } catch {
      return "One or more selectors are invalid. Please ensure all CSS selectors are correctly formatted.";
    }
  }

  const hasLeadingSlash = newEvent.applyToUrls
    ? newEvent.applyToUrls?.length > 0 &&
      newEvent.applyToUrls.some(
        (url) =>
          PreventUrlSlashList.includes(url.matchBy) &&
          url.value?.trim().startsWith("/")
      )
    : false;

  if (hasLeadingSlash) {
    return `Leading slashes are not allowed for ${describePreventUrlSlashMatchTypes()} match types.`;
  }

  const hasBlankDisallowedValue = newEvent.applyToUrls
    ? newEvent.applyToUrls?.length > 0 &&
      newEvent.applyToUrls.some(
        (url) =>
          DisallowBlankValueList.includes(url.matchBy) && !url.value?.trim()
      )
    : false;

  if (hasBlankDisallowedValue) {
    return "URL value cannot be blank for 'contains', 'starts with', 'ends with' and related match types.";
  }

  // Skip the settings comparison for scrollDepth events
  const areSettingsUnchanged =
    newEvent.type === "scrollDepth"
      ? false // Don't validate settings equality for scroll depth events
      : initialEvent.settings && Object.keys(initialEvent.settings).length === 0
        ? false // Skip evaluation if default settings are an empty object
        : Array.isArray(newEvent.settings)
          ? newEvent.settings.every(
              (setting, index) =>
                JSON.stringify(setting) ===
                JSON.stringify(initialEvent.settings?.[index])
            )
          : JSON.stringify(newEvent.settings) ===
            JSON.stringify(initialEvent.settings);

  if (areSettingsUnchanged) {
    return "Ensure all required fields are completed, and remove any unnecessary empty fields.";
  }

  const isNameInvalid = !newEvent.name?.trim();
  if (isNameInvalid) {
    return "Event name is required.";
  }

  if (newEvent.error) {
    return newEvent.error;
  }

  return ""; // No validation issues
};

export const isSaveButtonDisabled = (
  newEvent: CreateCustomEventDto | null | undefined
): boolean => {
  return !!getSaveValidationIssues(newEvent);
};

export const getCustomEventsError = (
  newEvent: CreateCustomEventDto | null | undefined
): string => {
  return getSaveValidationIssues(newEvent);
};

export const customEventIconMap: Record<CustomEventType, React.ElementType> = {
  pageView: NestDoorbellVisitor,
  productPageView: NestDoorbellVisitor,
  collectionPageView: NestDoorbellVisitor,
  clickEvent: WebTraffic,
  elementViewed: Visibility,
  scrollDepth: StatMinus,
  javascriptEvent: CodeBlocks,
  javascriptImportedEvent: CodeBlocks,
  addToCartCustomEvent: WebTraffic,
  beginCheckoutCustomEvent: WebTraffic,
  orderPlacedCustomEvent: WebTraffic,
  visitedAnyPdpCustomEvent: NestDoorbellVisitor,
  visitedAnyCollectionCustomEvent: NestDoorbellVisitor,
};

export const eventTypes = [
  {
    key: "productPageView",
    icon: NestDoorbellVisitor,
    title: "Visits to a Product Page",
    description: "Triggered when visitors land on one or more product pages",
    actionSubtitle:
      "Trigger event when visitor lands on any of the product page(s) below.",
    link: customEventsLink,
    lintTitle: "Custom Events - Overview",
  },
  {
    key: "collectionPageView",
    icon: NestDoorbellVisitor,
    title: "Visits to a Collection Page",
    description: "Triggered when visitors land on one or more collection pages",
    actionSubtitle:
      "Trigger event when visitor lands on any of the collection page(s) below.",
    link: customEventsLink,
    lintTitle: "Custom Events - Overview",
  },
  {
    key: "pageView",
    icon: NestDoorbellVisitor,
    title: "Visits to a URL",
    description: "Triggered when visitors land on one or more pages",
    actionSubtitle:
      "Trigger event when visitor lands on any of the page(s) below.",
    link: customEventsLink,
    lintTitle: "Custom Events - Overview",
  },
  {
    key: "clickEvent",
    icon: WebTraffic,
    title: "Click Interaction",
    description: "Triggered when visitors click on a page element",
    actionSubtitle:
      "Trigger event when visitor clicks on any of following page element(s).",
    link: customEventsLink,
    lintTitle: "Custom Events - Overview",
  },
  {
    key: "elementViewed",
    icon: Visibility,
    title: "Element Visible",
    description: "Triggered when a page element is in the visitor’s view",
    actionSubtitle: "Trigger when the following page element(s) are in view.",
    link: customEventsLink,
    lintTitle: "Custom Events - Overview",
  },
  {
    key: "scrollDepth",
    icon: StatMinus,
    title: "Scroll Depth",
    description: "Triggered when a visitor scrolls deep enough down a page",
    actionSubtitle:
      "Trigger when a visitor has scrolled to this percentage down the page.",
    link: customEventsLink,
    lintTitle: "Custom Events - Overview",
  },
  {
    key: "javascriptEvent",
    icon: CodeBlocks,
    title: "Custom JavaScript Event",
    description:
      "Triggered when a visitor performs an action defined via JavaScript",
    actionSubtitle:
      "Trigger event when a visitor performs an action defined via javascript you write.",
    link: customEventsLink,
    lintTitle: "Custom Events - Overview",
  },
  {
    key: "javascriptImportedEvent",
    icon: CodeBlocks,
    title: "Custom JavaScript Event",
    description:
      "Triggered when a visitor performs an action defined via JavaScript",
    actionSubtitle:
      "Trigger event when a visitor performs an action defined via javascript you write.",
    linkTitle: "Custom JavaScript Events",
    link: customJavascriptEventLink,
  },
];

export const createNewEvent = (
  eventType: CustomEventType
): CreateCustomEventDto => {
  switch (eventType) {
    case "pageView":
      return initPageViewEvent();
    case "productPageView":
      return initProductPageViewEvent();
    case "collectionPageView":
      return initCollectionPageViewEvent();
    case "clickEvent":
      return initClickEvent();
    case "elementViewed":
      return initElementViewedEvent();
    case "scrollDepth":
      return initScrollDepthEvent();
    case "javascriptEvent":
      return initJavascriptEvent();
    case "javascriptImportedEvent":
      return initJavascriptImportedEvent();
    default:
      throw new Error(`Unsupported event type: ${eventType}`);
  }
};

export const createCustomEventPayload = (
  newEvent: CreateCustomEventDto
): CreateCustomEventDto => {
  switch (newEvent.type) {
    case "pageView":
      return {
        ...newEvent,
        name: newEvent.name,
        description: newEvent.description ?? null,
        type: newEvent.type,
        // identifier: generateIdentifier(newEvent),
        settings: newEvent.settings || {},
        applyToUrls: newEvent.applyToUrls || null,
      };
    case "clickEvent":
      return {
        ...newEvent,
        name: newEvent.name,
        description: newEvent.description ?? null,
        type: newEvent.type,
        // identifier: generateIdentifier(newEvent),
        settings: newEvent.settings || {},
        applyToUrls: newEvent.applyToUrls || null,
      };
    case "elementViewed":
      return {
        ...newEvent,
        name: newEvent.name,
        description: newEvent.description ?? null,
        type: newEvent.type,
        // identifier: generateIdentifier(newEvent),
        settings: newEvent.settings || {},
        applyToUrls: newEvent.applyToUrls || null,
      };
    case "scrollDepth":
      return {
        ...newEvent,
        name: newEvent.name,
        description: newEvent.description ?? null,
        type: newEvent.type,
        // identifier: generateIdentifier(newEvent),
        settings: newEvent.settings || {},
        applyToUrls: newEvent.applyToUrls || null,
      };
    case "javascriptEvent":
      return {
        ...newEvent,
        name: newEvent.name,
        description: newEvent.description ?? null,
        type: newEvent.type,
        // identifier: generateIdentifier(newEvent),
        settings: newEvent.settings || {},
        applyToUrls: newEvent.applyToUrls || null,
      };
    case "productPageView":
      return {
        ...newEvent,
        name: newEvent.name,
        description: newEvent.description ?? null,
        type: newEvent.type,
        // identifier: generateIdentifier(newEvent),
        settings: newEvent.settings || {},
      };
    case "collectionPageView":
      return {
        ...newEvent,
        name: newEvent.name,
        description: newEvent.description ?? null,
        type: newEvent.type,
        // identifier:  generateIdentifier(newEvent),
        settings: newEvent.settings || {},
      };
    case "javascriptImportedEvent":
      return {
        ...newEvent,
        name: newEvent.name,
        description: newEvent.description ?? null,
        type: newEvent.type,
        identifier: newEvent.identifier || "",
        settings: {
          registered: newEvent.settings?.registered ?? false,
        },
        applyToUrls: null,
      };
  }
};
